// Commission corrections: shared React pieces used by the admin Commission tab,
// the admin rep-detail Commission sub-tab and the rep portal Commission page.
// Every write here goes through the Supabase-first helpers (awaited, errors
// shown and logged); nothing in this file touches localStorage.
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  supabase, listCorrections, listCommissionPaymentsPaged, subscribeRepCommissionCorrections,
  subscribeRepCommissionPayments, waiveCorrection, releaseCorrectionClaim, recordCorrectionForInvoice, getInvoiceStateForCorrection,
} from "./supabase";
import { runCommissionPayout, describePayoutResult } from "./commissionPayout";
import { netPayout } from "./commissionCorrections";
import { fmtSignedMoney, correctionKindLabel } from "./commissionCorrectionsView";

const K = {
  ink: "#0F172A", inkMid: "#334155", inkLight: "#64748B", border: "#E2E8F0", surface: "#F8FAFC", white: "#FFFFFF",
  red: "#B91C1C", redLight: "#FEE2E2", amber: "#B45309", amberLight: "#FEF3C7", green: "#166534", greenLight: "#DCFCE7",
  blue: "#1560BD", blueLight: "#E8F0FE", teal: "#0D7A5F",
};
const pill = (bg, fg) => ({ fontSize: 10, fontWeight: 500, padding: "2px 8px", borderRadius: 8, background: bg, color: fg, whiteSpace: "nowrap" });
const btn = { background: K.blueLight, color: K.blue, border: "none", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: "pointer" };
const btnPrimary = { background: K.blue, color: K.white, border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };
const btnSecondary = { background: "transparent", color: K.ink, border: "1px solid " + K.border, padding: "9px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer" };
const input = { width: "100%", padding: "9px 12px", border: "1px solid " + K.border, borderRadius: 8, fontSize: 13, color: K.ink, background: K.white, outline: "none", boxSizing: "border-box" };
const money = (n) => "$" + (Number(n) || 0).toFixed(2);
const dateOf = (iso) => { const d = iso ? new Date(iso) : null; return d && !isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"; };
const signColor = (n) => (Number(n) < 0 ? K.red : K.green);

// ── data hook ────────────────────────────────────────────────────────────────
// Loads the corrections and the COMPLETE (paged) payment rows on mount, again
// on every realtime event (debounced) and on reload(). A failed refresh keeps
// the last good data (never an empty list) and reports `error`. ready is true
// only when both reads have succeeded at least once and the last one did too:
// the scan and the anchoring logic must not run on a partial picture.
//   withPayments:false  skip the paged payments read + subscription (the rep
//                       portal reuses the paid rows it already loads).
//   repSafe:true        rep portal: explicit column list, no admin identity /
//                       internal note columns.
//   enabled:false       nothing is read or subscribed and the state is reset
//                       (the rep-detail Commission sub-tab only loads while open).
const INITIAL_LEDGER = { loaded: false, corrections: [], paidRows: null, missingTable: false, error: "" };
export function useCommissionLedger({ withPayments = true, repSafe = false, enabled = true } = {}) {
  const [state, setState] = useState(INITIAL_LEDGER);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    if (!enabled) { setState(INITIAL_LEDGER); return undefined; }
    let cancelled = false;
    let timer = null;
    const load = async () => {
      const mine = ++seq.current;
      let c; let p = null;
      try {
        [c, p] = await Promise.all([listCorrections(repSafe ? { repSafe: true } : {}), withPayments ? listCommissionPaymentsPaged({}) : Promise.resolve(null)]);
      } catch (e) {
        console.warn("[useCommissionLedger] load threw:", e?.message || e);
        if (!cancelled && mine === seq.current) setState((prev) => ({ ...prev, loaded: true, error: e?.message || String(e) }));
        return;
      }
      if (cancelled || mine !== seq.current) return;
      setState((prev) => ({
        loaded: true,
        corrections: c.ok ? c.rows : prev.corrections,
        missingTable: !!c.missingTable,
        paidRows: p ? (p.ok ? p.rows : prev.paidRows) : prev.paidRows,
        error: !c.ok ? c.error : (p && !p.ok ? p.error : ""),
      }));
    };
    const later = () => { if (timer) clearTimeout(timer); timer = setTimeout(load, 400); };
    load();
    const unC = subscribeRepCommissionCorrections(later);
    const unP = withPayments ? subscribeRepCommissionPayments(later) : () => {};
    return () => { cancelled = true; if (timer) clearTimeout(timer); unC && unC(); unP && unP(); };
  }, [withPayments, repSafe, enabled, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  const ready = enabled && state.loaded && !state.error && (!withPayments || Array.isArray(state.paidRows));
  return { ...state, ready, reload };
}

// True below ~520px (phone width): the Corrections card stacks instead of using
// its fixed six-column grid.
const narrowNow = (limit) => { try { return typeof window !== "undefined" && window.innerWidth < limit; } catch (e) { console.warn("[useNarrow] width read failed:", e?.message || e); return false; } };
export function useNarrow(limit = 520) {
  const [narrow, setNarrow] = useState(() => narrowNow(limit));
  useEffect(() => {
    const on = () => setNarrow(narrowNow(limit));
    try { window.addEventListener("resize", on); } catch (e) { console.warn("[useNarrow] resize listener failed:", e?.message || e); }
    return () => { try { window.removeEventListener("resize", on); } catch (e) { console.warn("[useNarrow] teardown failed:", e?.message || e); } };
  }, [limit]);
  return narrow;
}

export async function currentAdminIdentity() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) { console.warn("[currentAdminIdentity] getUser failed:", error.message); return { id: null, email: null }; }
    return { id: (data && data.user && data.user.id) || null, email: (data && data.user && data.user.email) || null };
  } catch (e) {
    console.warn("[currentAdminIdentity] threw:", e?.message || e);
    return { id: null, email: null };
  }
}

// ── Corrections card (admin per rep / rep portal) ────────────────────────────
// admin mode also lists ORPHANED settled corrections (summary.orphans: the claim
// never produced a payout AND is older than 10 minutes) with an amber note and a
// Re-open button (releaseCorrectionClaim, awaited, logged, errors shown); they
// are excluded from the settled totals until re-opened. A younger claim with no
// payment rows yet is "Settling..." (a payout in flight): no Re-open, not an
// orphan. In rep mode an orphan reads "Pending re-open" and a young one
// "Settling...", never Settled / paid. Rep mode never renders waive / settlement
// notes or admin identities. Below ~520px the card stacks (amount and status on
// their own row) instead of using the fixed six-column grid.
export function CorrectionsCard({ summary, mode = "admin", title, onChanged, onSettle, settleLabel }) {
  const [waivingId, setWaivingId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reopenErr, setReopenErr] = useState("");
  const narrow = useNarrow();
  const list = [...(summary.all || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const inert = summary.inert || [];
  const orphanIds = new Set((summary.orphans || []).map((c) => String(c.id)));
  const settlingIds = new Set((summary.settling || []).map((c) => String(c.id)));
  if (list.length === 0 && inert.length === 0) return null;
  const admin = mode === "admin";

  const doWaive = async (c) => {
    if (busy) return;
    if (!note.trim()) { setErr("A note is required to waive a correction."); return; }
    setBusy(true); setErr("");
    try {
      const who = await currentAdminIdentity();
      await waiveCorrection(c.id, note, who.email || who.id);
      setWaivingId(null); setNote("");
      if (onChanged) onChanged();
    } catch (e) {
      console.warn("[CorrectionsCard] waive failed for", c.id, ":", e?.message || e);
      setErr(e?.message || "Could not waive this correction.");
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };

  const doReopen = async (c) => {
    if (busy) return;
    if (!window.confirm("Re-open the " + fmtSignedMoney(c.delta) + " correction for " + c.invoice_id + "? Its payout never wrote any payment row, so it goes back to Open and will be netted into the next payout.")) return;
    setBusy(true); setReopenErr("");
    try {
      const r = await releaseCorrectionClaim([c.id], c.settled_batch_id);
      if (!r.ok) {
        console.warn("[CorrectionsCard] re-open failed for", c.id, "batch", c.settled_batch_id, ":", r.error);
        setReopenErr("Could not re-open this correction: " + (r.error || "unknown error") + ". If a payout row exists for that batch it was in fact paid; refresh and check.");
      }
    } catch (e) {
      console.warn("[CorrectionsCard] re-open threw for", c.id, ":", e?.message || e);
      setReopenErr("Could not re-open this correction: " + (e?.message || e));
    } finally {
      setBusy(false);
      if (onChanged) onChanged();
    }
  };

  const statusCell = (c) => {
    const orphan = orphanIds.has(String(c.id));
    const settling = settlingIds.has(String(c.id));
    return (
      <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {c.status === "open" && <span style={pill(K.amberLight, K.amber)}>Open</span>}
        {c.status === "settled" && !orphan && !settling && <span style={pill(K.greenLight, K.green)} title={c.settled_batch_id || ""}>Settled {dateOf(c.settled_at)}</span>}
        {c.status === "settled" && settling && <span style={pill(K.blueLight, K.blue)} title="The payout that claimed this correction has not written its payment rows yet. It can be re-opened only if it is still unpaid 10 minutes after the claim.">Settling...</span>}
        {c.status === "settled" && orphan && <span style={pill(K.amberLight, K.amber)} title={admin ? (c.settled_batch_id || "") : "The payout that claimed this correction was never recorded. Your admin will re-open it so it is netted into your next payout."}>{admin ? "Claimed, never paid out" : "Pending re-open"}</span>}
        {c.status === "waived" && <span style={pill("#F3F4F6", "#374151")} title={admin ? (c.settled_notes || "") : ""}>Waived {dateOf(c.settled_at)}</span>}
        {admin && c.status === "open" && waivingId !== c.id && <button style={btn} onClick={() => { setWaivingId(c.id); setNote(""); setErr(""); }}>Waive</button>}
        {admin && orphan && <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => doReopen(c)}>Re-open</button>}
      </span>
    );
  };

  const cols = admin ? "86px 100px 84px 1fr 90px 190px" : "86px 100px 84px 1fr 90px 150px";
  const wrapText = { overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 };
  return (
    <div style={{ background: K.white, border: "1px solid " + K.border, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "13px 18px", borderBottom: "1px solid " + K.border, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: K.ink }}>{title || "Commission corrections"}</span>
        <span style={{ fontSize: 11, color: K.inkLight }}>
          Open <strong style={{ color: signColor(summary.openTotal) }}>{fmtSignedMoney(summary.openTotal)}</strong>
          {" · "}Settled <strong style={{ color: K.ink }}>{fmtSignedMoney(summary.settledTotal)}</strong>
          {summary.waived.length > 0 && <span>{" · "}Waived {fmtSignedMoney(summary.waivedTotal)}</span>}
        </span>
      </div>
      <div style={{ padding: "8px 18px", fontSize: 11, color: K.inkMid, background: K.surface, borderBottom: "1px solid " + K.border }}>
        {admin
          ? "A correction is recorded when an invoice is adjusted or voided after its commission was paid. The paid record itself is never changed. Open corrections are netted into the next payout. The reason is shown to the rep."
          : "When an invoice is changed or voided after its commission was paid, the payout you already received is not altered. A correction line (plus or minus) is added here and netted into your next payout. The reason shown is visible to you."}
      </div>
      {admin && orphanIds.size > 0 && (
        <div style={{ padding: "8px 18px", fontSize: 11, color: K.amber, background: K.amberLight, borderBottom: "1px solid " + K.border }}>
          {orphanIds.size} correction{orphanIds.size === 1 ? " was" : "s were"} claimed by a payout that never wrote its payment rows (claim never paid out, more than 10 minutes ago), for a total of {fmtSignedMoney(summary.orphanTotal)}. {orphanIds.size === 1 ? "It is" : "They are"} NOT counted as paid out. Use Re-open so {orphanIds.size === 1 ? "it is" : "they are"} netted into the next payout.
        </div>
      )}
      {!admin && orphanIds.size > 0 && (
        <div style={{ padding: "8px 18px", fontSize: 11, color: K.amber, background: K.amberLight, borderBottom: "1px solid " + K.border }}>
          {orphanIds.size} correction{orphanIds.size === 1 ? " is" : "s are"} pending re-open by your admin ({fmtSignedMoney(summary.orphanTotal)}). {orphanIds.size === 1 ? "It is" : "They are"} not counted as paid or as owed until then.
        </div>
      )}
      {reopenErr && <div style={{ padding: "8px 18px", fontSize: 11, color: K.red, background: K.redLight, borderBottom: "1px solid " + K.border }}>{reopenErr}</div>}
      {!narrow && (
        <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "8px 18px", fontSize: 10, fontWeight: 600, color: K.inkLight, borderBottom: "1px solid " + K.border }}>
          <span>Date</span><span>Invoice</span><span>Type</span><span>{admin ? "Reason (visible to the rep)" : "Reason (visible to you)"}</span><span style={{ textAlign: "right" }}>Amount</span><span>Status</span>
        </div>
      )}
      {list.map((c) => (
        <div key={c.id}>
          {narrow ? (
            <div style={{ padding: "10px 14px", fontSize: 12, borderBottom: "1px solid " + K.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                <span style={{ fontWeight: 600, color: K.blue, ...wrapText }} title={c.invoice_id}>{c.invoice_id} <span style={{ fontWeight: 400, color: K.inkMid }}>· {correctionKindLabel(c)}</span></span>
                <span style={{ fontWeight: 700, color: signColor(c.delta), whiteSpace: "nowrap" }}>{fmtSignedMoney(c.delta)}</span>
              </div>
              <div style={{ color: K.inkLight, fontSize: 11, marginTop: 2 }}>{dateOf(c.created_at)}</div>
              <div style={{ color: K.ink, marginTop: 4, ...wrapText }}>{c.reason}</div>
              <div style={{ marginTop: 6 }}>{statusCell(c)}</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "9px 18px", fontSize: 12, alignItems: "center", borderBottom: "1px solid " + K.border }}>
              <span style={{ color: K.inkLight }}>{dateOf(c.created_at)}</span>
              <span style={{ fontWeight: 600, color: K.blue, overflow: "hidden", textOverflow: "ellipsis" }} title={c.invoice_id}>{c.invoice_id}</span>
              <span style={{ color: K.inkMid }}>{correctionKindLabel(c)}</span>
              <span style={{ color: K.ink, ...wrapText }} title={c.reason}>{c.reason}</span>
              <span style={{ fontWeight: 700, color: signColor(c.delta), textAlign: "right" }}>{fmtSignedMoney(c.delta)}</span>
              {statusCell(c)}
            </div>
          )}
          {c.status === "settled" && c.settled_batch_id && (
            <div style={{ padding: "0 18px 8px", fontSize: 10, color: K.inkLight }}>
              Settled in payout {c.settled_batch_id}{c.settled_method ? " (" + c.settled_method + ")" : ""}
            </div>
          )}
          {admin && c.status === "waived" && c.settled_notes && (
            <div style={{ padding: "0 18px 8px", fontSize: 10, color: K.inkLight, ...wrapText }}>Waived (internal note): {c.settled_notes}</div>
          )}
          {admin && waivingId === c.id && (
            <div style={{ padding: "8px 18px 12px", background: K.surface, borderBottom: "1px solid " + K.border }}>
              <div style={{ fontSize: 11, color: K.inkMid, marginBottom: 6 }}>Waive {fmtSignedMoney(c.delta)} for {c.invoice_id}. It will no longer be netted into a payout. A note is required. The note is internal: it is not shown to the rep in the portal.</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input style={{ ...input, flex: "1 1 200px", width: "auto" }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal reason (not shown to the rep)" />
                <button style={{ ...btnPrimary, padding: "8px 14px", opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => doWaive(c)}>{busy ? "Saving..." : "Confirm waive"}</button>
                <button style={{ ...btnSecondary, padding: "8px 14px" }} disabled={busy} onClick={() => { setWaivingId(null); setErr(""); }}>Cancel</button>
              </div>
              {err && <div style={{ fontSize: 11, color: K.red, marginTop: 6 }}>{err}</div>}
            </div>
          )}
        </div>
      ))}
      {inert.length > 0 && (
        <div style={{ padding: "8px 18px", fontSize: 11, color: K.inkLight, background: K.surface }}>
          {inert.length} open correction{inert.length === 1 ? "" : "s"} for a payout that was undone {inert.length === 1 ? "is" : "are"} inactive and not counted.
        </div>
      )}
      {admin && summary.open.length > 0 && onSettle && (
        <div style={{ padding: "10px 18px", display: "flex", justifyContent: "flex-end", borderTop: "1px solid " + K.border }}>
          <button style={btn} onClick={onSettle}>{settleLabel || "Settle corrections only"}</button>
        </div>
      )}
    </div>
  );
}

// ── Mark Paid modal section (shared by BOTH Mark Paid sites) ─────────────────
// open: the rep's open (anchored) corrections; selected: ticked ids, or null when
// not initialised yet; invoiceCommission: what the invoices in this payout add up to.
//   ready  the paged payments and the corrections are both loaded. Until then
//          nothing is listed or ticked and the caller keeps Confirm disabled.
//   error  the last ledger read failed: a red note with Retry replaces the list
//          and the corrections cannot be ticked (the caller offers an explicit
//          invoices-only confirm).
// Default ticks are set ONCE, when the list first renders on a ready ledger (not
// captured at modal-open); the component lives exactly as long as the modal.
export function CorrectionsPayoutSection({ open, selected, onChange, invoiceCommission, ready = true, error = "", onRetry }) {
  const inited = useRef(false);
  const list = ready && !error ? (open || []) : [];
  useEffect(() => {
    if (inited.current || !ready || error) return;
    inited.current = true;
    if (selected == null) onChange((open || []).map((c) => c.id));
  });
  if (error) {
    return (
      <div style={{ border: "1px solid " + K.red, borderRadius: 10, padding: "12px 14px", marginBottom: 14, background: K.redLight, fontSize: 12, color: K.red }}>
        Open corrections could not be loaded ({error}), so they are NOT included in this payout and cannot be ticked.
        {onRetry && <button style={{ ...btn, marginLeft: 8 }} onClick={onRetry}>Retry</button>}
      </div>
    );
  }
  if (!ready) {
    return <div style={{ fontSize: 11, color: K.inkLight, marginBottom: 14 }}>Checking for open corrections...</div>;
  }
  if (list.length === 0) return null;
  const sel = new Set((selected || []).map(String));
  const picked = list.filter((c) => sel.has(String(c.id)));
  const np = netPayout({ invoiceCommission, selectedCorrections: picked });
  const toggle = (c) => {
    const next = new Set(sel);
    if (next.has(String(c.id))) next.delete(String(c.id)); else next.add(String(c.id));
    onChange(list.filter((x) => next.has(String(x.id))).map((x) => x.id));
  };
  return (
    <div style={{ border: "1px solid " + K.border, borderRadius: 10, padding: "12px 14px", marginBottom: 14, background: K.surface }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: K.ink, marginBottom: 6 }}>Open corrections for this rep</div>
      {list.map((c) => (
        <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: K.inkMid, marginBottom: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={sel.has(String(c.id))} onChange={() => toggle(c)} style={{ marginTop: 2 }} />
          <span style={{ flex: 1, overflowWrap: "anywhere" }}>{c.invoice_id} · {correctionKindLabel(c)} · {c.reason}</span>
          <strong style={{ color: signColor(c.delta) }}>{fmtSignedMoney(c.delta)}</strong>
        </label>
      ))}
      <div style={{ borderTop: "1px solid " + K.border, marginTop: 8, paddingTop: 8, fontSize: 12, color: K.inkMid }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Invoice payout</span><span>{money(np.invoiceCommission)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Corrections</span><span style={{ color: signColor(np.correctionsTotal) }}>{fmtSignedMoney(np.correctionsTotal)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: K.ink }}><span>Net payout</span><span>{money(np.negative ? np.invoiceCommission : np.net)}</span></div>
      </div>
      {np.negative && (
        <div style={{ fontSize: 11, color: K.amber, marginTop: 8 }}>
          The net would be negative ({fmtSignedMoney(np.net)}). These corrections will NOT be settled: they stay open as a balance carried forward. The invoices are still paid as usual.
        </div>
      )}
    </div>
  );
}

// ── Correction-only payout modal (rep has corrections but nothing pending) ────
export function CorrectionOnlyModal({ repId, repName, open, onClose, onDone }) {
  const [sel, setSel] = useState(() => (open || []).map((c) => c.id));
  const [form, setForm] = useState({ method: "ACH", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const confirm = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const admin = await currentAdminIdentity();
      const res = await runCommissionPayout({ repId, rows: [], batchId: null, method: form.method, notes: form.notes, admin, selectedCorrectionIds: sel });
      if (!res.ok) { setErr(describePayoutResult(res)); if (onDone) onDone(res); return; }
      if (onDone) onDone(res);
      onClose();
    } catch (e) {
      console.warn("[CorrectionOnlyModal] payout threw for rep", repId, ":", e?.message || e);
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  };
  const picked = (open || []).filter((c) => sel.some((id) => String(id) === String(c.id)));
  const np = netPayout({ invoiceCommission: 0, selectedCorrections: picked });
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: K.white, borderRadius: 16, padding: "28px 32px", width: 480, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: K.ink, marginBottom: 4 }}>Settle corrections</div>
        <div style={{ fontSize: 13, color: K.inkLight, marginBottom: 12 }}>{repName}. No invoice rows are written for a corrections-only payout.</div>
        <CorrectionsPayoutSection open={open} selected={sel} onChange={setSel} invoiceCommission={0} />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: K.inkMid, marginBottom: 5 }}>Payment method *</label>
          <select style={input} value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
            {["ACH", "Check", "Wire", "Zelle", "Cash", "PayPal"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: K.inkMid, marginBottom: 5 }}>Notes (optional)</label>
          <input style={input} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Reference number, memo, etc." />
        </div>
        {err && <div style={{ fontSize: 12, color: K.red, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} onClick={confirm} disabled={busy || sel.length === 0 || np.negative}>{busy ? "Saving..." : "Confirm settlement"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Correction-needed scan card ──────────────────────────────────────────────
// scan: { proposals, flagged } from findNeededCorrections (or null while the
// picture is incomplete). Proposes only; every line needs its own confirm click.
// The confirm re-reads the invoice from the database and passes the delta the
// admin approved (expectedDelta): if the fresh delta differs by more than 0.005
// NOTHING is inserted, the line shows the refreshed proposal and needs a second
// click (the second click approves the refreshed amount).
export function CorrectionScanCard({ scan, notReadyReason, onRecorded }) {
  const [busyId, setBusyId] = useState(null);
  const [msgs, setMsgs] = useState({});
  const [fresh, setFresh] = useState({});   // invoiceId -> { delta, newTotal, isVoid } refreshed proposal
  const setMsg = (id, m) => setMsgs((prev) => ({ ...prev, [id]: m }));
  if (!scan) {
    if (!notReadyReason) return null;
    return <div style={{ fontSize: 11, color: K.amber, background: K.amberLight, border: "1px solid " + K.amber, borderRadius: 8, padding: "8px 12px", margin: "0 0 12px" }}>{notReadyReason}</div>;
  }
  const legacyFlags = scan.flagged.filter((f) => f.code === "legacy_only");
  const otherFlags = scan.flagged.filter((f) => f.code !== "legacy_only");
  if (scan.proposals.length === 0 && scan.flagged.length === 0) return null;

  const confirm = async (p) => {
    if (busyId) return;
    setBusyId(p.invoiceId); setMsg(p.invoiceId, null);
    try {
      const st = await getInvoiceStateForCorrection(p.invoiceId);
      if (!st.ok) { setMsg(p.invoiceId, { bad: true, text: "Could not read the invoice from the database: " + st.error }); return; }
      if (!st.found) { setMsg(p.invoiceId, { bad: true, text: "This invoice is not in the database (portal_orders), so its current total cannot be confirmed." }); return; }
      if (!st.cancelled && !(Number.isFinite(st.total) && st.total >= 0)) { setMsg(p.invoiceId, { bad: true, text: "The invoice total in the database is not a valid amount." }); return; }
      const admin = await currentAdminIdentity();
      const approved = fresh[p.invoiceId] ? fresh[p.invoiceId].delta : p.delta;
      const res = await recordCorrectionForInvoice({
        invoiceId: p.invoiceId, newTotal: st.total, isVoid: st.cancelled,
        reason: st.cancelled ? "Invoice voided after payout" : "Invoice adjusted after payout",
        source: "reconcile", admin, expectedDelta: approved,
      });
      if (res.ok && res.inserted) { setFresh((prev) => { const n = { ...prev }; delete n[p.invoiceId]; return n; }); setMsg(p.invoiceId, { text: "Correction recorded: " + fmtSignedMoney(res.inserted.delta) }); }
      else if (res.ok && res.none) setMsg(p.invoiceId, { text: "Nothing to record: the database shows the paid commission already matches this invoice." });
      else if (res.changed) {
        setFresh((prev) => ({ ...prev, [p.invoiceId]: { delta: res.freshDelta, newTotal: st.total, isVoid: st.cancelled } }));
        setMsg(p.invoiceId, { bad: true, text: "The invoice changed since the scan (you approved " + fmtSignedMoney(approved) + ", the database now gives " + fmtSignedMoney(res.freshDelta) + "). Nothing was recorded. The proposal is refreshed: review it and click Record correction again to approve the new amount." });
      }
      else if (res.refused) setMsg(p.invoiceId, { bad: true, text: res.reason });
      else setMsg(p.invoiceId, { bad: true, text: "Could not record the correction: " + (res.error || "unknown error") });
      if (onRecorded) onRecorded();
    } catch (e) {
      console.warn("[CorrectionScanCard] confirm failed for", p.invoiceId, ":", e?.message || e);
      setMsg(p.invoiceId, { bad: true, text: e?.message || String(e) });
    } finally { setBusyId(null); }
  };

  return (
    <div style={{ background: K.white, border: "1px solid " + K.amber, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "13px 18px", borderBottom: "1px solid " + K.border, background: K.amberLight, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: K.amber }}>{scan.proposals.length > 0 ? "Correction needed (" + scan.proposals.length + ")" : "Commission review"}</span>
        <span style={{ fontSize: 11, color: K.inkMid }}>{scan.proposals.length > 0 ? "These invoices changed after their commission was paid. Nothing is recorded until you confirm each line." : "Nothing to record right now."}</span>
      </div>
      {scan.proposals.map((p) => {
        const m = msgs[p.invoiceId];
        const fr = fresh[p.invoiceId];
        const shownDelta = fr ? fr.delta : p.delta;
        const shownVoid = fr ? fr.isVoid : p.isVoid;
        const shownTotal = fr ? fr.newTotal : p.newTotal;
        return (
          <div key={p.invoiceId} style={{ padding: "10px 18px", borderBottom: "1px solid " + K.border, display: "grid", gridTemplateColumns: "minmax(0,1fr) 110px 130px", gap: 10, alignItems: "center", fontSize: 12 }}>
            <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <div style={{ fontWeight: 600, color: K.ink }}>{p.invoiceId} <span style={{ fontWeight: 400, color: K.inkLight }}>· {p.rep_name || "rep"}</span>{fr && <span style={{ ...pill(K.amberLight, K.amber), marginLeft: 6 }}>Refreshed, review</span>}</div>
              <div style={{ fontSize: 11, color: K.inkLight }}>{shownVoid ? "Invoice voided" : "Invoice total changed to $" + Number(shownTotal).toFixed(2)} after payout {p.paid_batch_id ? "(" + p.paid_batch_id + ")" : ""}. Paid {money(p.paid_amount)}{fr ? "." : "; commission should now be " + money(p.new_commission) + "."}</div>
              {m && <div style={{ fontSize: 11, color: m.bad ? K.red : K.green, marginTop: 4 }}>{m.text}</div>}
            </div>
            <span style={{ fontWeight: 700, color: signColor(shownDelta), textAlign: "right" }}>{fmtSignedMoney(shownDelta)}</span>
            <button style={{ ...btn, opacity: busyId ? 0.6 : 1 }} disabled={!!busyId} onClick={() => confirm(p)}>{busyId === p.invoiceId ? "Recording..." : "Record correction"}</button>
          </div>
        );
      })}
      {otherFlags.length > 0 && (
        <div style={{ padding: "10px 18px", background: K.surface, fontSize: 11, color: K.inkMid, borderBottom: "1px solid " + K.border }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Skipped, needs manual review ({otherFlags.length})</div>
          {otherFlags.map((f) => <div key={f.invoiceId + f.code}>{f.invoiceId}: {f.reason}</div>)}
        </div>
      )}
      {legacyFlags.length > 0 && (
        <div style={{ padding: "10px 18px", background: K.surface, fontSize: 11, color: K.inkMid }}>
          {legacyFlags.length} invoice{legacyFlags.length === 1 ? " has" : "s have"} a legacy payout that is not migrated to Supabase yet. Corrections for {legacyFlags.length === 1 ? "it are" : "them are"} refused until the backfill has run (open this tab on the browser that holds the legacy payout).
        </div>
      )}
    </div>
  );
}

// Same message text for both Mark Paid sites.
export { describePayoutResult, runCommissionPayout };
